// Lightweight, fully original PartyBuddy avatar renderer — a handful of
// simple composable SVG layers (head/hair/eyes/eyebrows/mouth/facial hair/
// glasses), parameterized by a small config object. Deliberately NOT a
// pixel-perfect character system (no external art assets, no third-party
// avatar service) — this is the deliberately modest MVP layer set called
// for by the spec, expandable later without changing the config shape.
export const AVATAR_SKIN_TONES = ["#ffdbac", "#f1c27d", "#e0ac69", "#c68642", "#8d5524"];
export const AVATAR_HAIR_COLORS = ["#1f1f1f", "#4b2e1e", "#8b5a2b", "#d4a017", "#e5e7eb", "#b91c1c"];
export const AVATAR_EYE_COLORS = ["#4b2e1e", "#2563eb", "#16a34a", "#78716c"];
export const AVATAR_HAIR_STYLES = ["bald", "short", "long", "curly"];
export const AVATAR_FACIAL_HAIR = ["none", "mustache", "beard"];
export const AVATAR_EYEBROWS = ["thin", "thick"];
export const AVATAR_EYE_STYLES = ["round", "narrow"];
export const AVATAR_MOUTHS = ["smile", "neutral", "open"];
export const AVATAR_GLASSES = ["none", "round", "square"];
export const AVATAR_BASES = ["female", "male"];

export const DEFAULT_AVATAR_CONFIG = {
  base: "female",
  skinTone: AVATAR_SKIN_TONES[0],
  hairStyle: "short",
  hairColor: AVATAR_HAIR_COLORS[0],
  facialHair: "none",
  eyebrows: "thin",
  eyeStyle: "round",
  eyeColor: AVATAR_EYE_COLORS[0],
  mouth: "smile",
  glasses: "none",
};

function Hair({ style, color }) {
  if (style === "bald") return null;
  if (style === "long") {
    // Top cap (same safe shape as "short") plus two side locks that hang
    // down along the OUTSIDE edges of the face oval (well clear of the
    // eyes/mouth, which all sit within x≈43-67) — avoids the hair
    // overlapping/clipping into facial features at any size.
    return (
      <>
        <path d="M28 44c2-18 14-28 27-28s25 10 27 28c-8-8-46-8-54 0z" fill={color} />
        <path d="M24 46c-3 20-2 42 5 56 2-5 3-11 2-17-2-14-4-26-1-39z" fill={color} />
        <path d="M86 46c3 20 2 42-5 56-2-5-3-11-2-17 2-14 4-26 1-39z" fill={color} />
      </>
    );
  }
  if (style === "curly") {
    return (
      <>
        <circle cx="34" cy="34" r="10" fill={color} />
        <circle cx="48" cy="26" r="11" fill={color} />
        <circle cx="62" cy="26" r="11" fill={color} />
        <circle cx="76" cy="34" r="10" fill={color} />
        <circle cx="55" cy="20" r="10" fill={color} />
      </>
    );
  }
  // short (default)
  return <path d="M28 44c2-18 14-28 27-28s25 10 27 28c-8-8-46-8-54 0z" fill={color} />;
}

function FacialHair({ style, color }) {
  if (style === "mustache") {
    return <path d="M42 70c6 4 20 4 26 0-2 4-24 4-26 0z" fill={color} />;
  }
  if (style === "beard") {
    return <path d="M32 62c2 18 14 28 23 28s21-10 23-28c-6 10-40 10-46 0z" fill={color} opacity="0.9" />;
  }
  return null;
}

function Eyebrows({ style, color }) {
  const height = style === "thick" ? 4 : 2;
  return (
    <>
      <rect x="37" y="48" width="12" height={height} rx={height / 2} fill={color} />
      <rect x="61" y="48" width="12" height={height} rx={height / 2} fill={color} />
    </>
  );
}

function Eyes({ style, color }) {
  const ry = style === "narrow" ? 2.5 : 4;
  return (
    <>
      <ellipse cx="43" cy="58" rx="4" ry={ry} fill={color} />
      <ellipse cx="67" cy="58" rx="4" ry={ry} fill={color} />
    </>
  );
}

function Mouth({ style }) {
  if (style === "open") return <ellipse cx="55" cy="76" rx="7" ry="5" fill="#7f1d1d" />;
  if (style === "neutral") return <rect x="46" y="76" width="18" height="2.5" rx="1.25" fill="#7f1d1d" />;
  return <path d="M45 74c4 6 16 6 20 0-2 8-18 8-20 0z" fill="#7f1d1d" />;
}

function Glasses({ style }) {
  if (style === "none") return null;
  const rx = style === "square" ? 0 : 9;
  return (
    <g fill="none" stroke="#1f2937" strokeWidth="2.5">
      <rect x="34" y="50" width="18" height="14" rx={rx} />
      <rect x="58" y="50" width="18" height="14" rx={rx} />
      <line x1="52" y1="56" x2="58" y2="56" />
    </g>
  );
}

// Composable custom avatar. `config` matches the shape stored in the user's
// Firestore profile (`avatarConfig`) — see AuthContext/Profile.js.
function AvatarSVG({ config, size = 40, className = "" }) {
  const cfg = { ...DEFAULT_AVATAR_CONFIG, ...config };
  const dimension = typeof size === "number" ? `${size}px` : size;

  return (
    <svg
      viewBox="0 0 110 110"
      width={dimension}
      height={dimension}
      className={`shrink-0 rounded-full border border-slate-700 bg-slate-800 ${className}`}
      role="img"
      aria-label="PartyBuddy avatar"
    >
      <circle cx="55" cy="55" r="55" fill="#0f172a" />
      {/* Head */}
      <ellipse cx="55" cy="62" rx="27" ry="30" fill={cfg.skinTone} />
      <FacialHair style={cfg.facialHair} color={cfg.hairColor} />
      <Eyebrows style={cfg.eyebrows} color={cfg.hairColor} />
      <Eyes style={cfg.eyeStyle} color={cfg.eyeColor} />
      <Mouth style={cfg.mouth} />
      <Glasses style={cfg.glasses} />
      <Hair style={cfg.hairStyle} color={cfg.hairColor} />
    </svg>
  );
}

export default AvatarSVG;
